import { system, LookupStorage } from "portalframework";
import * as ko from "knockout";
import * as $ from "jquery";
import * as toastr from "toastr";
import * as _ from "lodash";
import labels from "app/labels";
import * as moment from "moment";
import { AttachmentResultDTO, downloadAttachmentFromBase64 } from "services";
import {
  MeterReadingContract,
  MeterReadingContractDetail,
  MeterReadingSearchCriteria,
  MeterReadingValue,
  searchResult,
  uploadMeterReading,
} from "organized";
import { portalHttpService } from "./_common/httpService";
import { DISPLAY_DATETIME_DEFAULT_FORMAT } from "app/custom-observables";
import { BaseService } from "./_common/_baseService";
class MeterReadingsService extends BaseService {
  constructor() {
    super("meterreading");
  }

  public getContractsScreenData = async (): Promise<void> => {
    const requiredLookups = ["CONTRACT_STATUS", "CONTR_TYPE", "APPROVAL_STATUS"];
    const lookupsToFetch = _.difference(requiredLookups, LookupStorage.lookupStorage.lookupNames());
    const data = ko.toJSON({ requiredLookups: lookupsToFetch });
    const lookups = await portalHttpService.post<any>(`${this.baseApiUrl()}/GetContractScreenData`, data);

    lookupsToFetch.forEach((lookupName) => {
      let list = lookups.results[lookupName];
      if (list) {
        LookupStorage.lookupStorage.add(new LookupStorage.Lookup(lookupName, list));
      }
    });
  };

  public searchContracts = async (criteria: MeterReadingSearchCriteria) => {
    const data = await portalHttpService.post<any>(`${this.baseApiUrl()}/SearchContracts`, ko.toJSON(criteria));
    if (data.count != 0) {
      return {
        data: data.result.map((contract) => {
          return new MeterReadingContract(contract);
        }),
        count: data.count,
      };
    } else {
      throw new Error(labels.descr("cc_P_NO_RECORDS_FOUND"));
    }
  };

  public getContractDetails = async (contract_id, contract_version) => {
    let data = ko.toJSON({ contract_id: contract_id, contract_version: contract_version });
    const result = await portalHttpService.post<any>(`${this.baseApiUrl()}/GetContractDetails`, data);
    if (result.contract) {
      return new MeterReadingContractDetail(result);
    }
    throw new Error(labels.descr("cc_P_NO_RECORDS_FOUND"));
  };

  public searchMeterReadings = async (
    criteria: MeterReadingSearchCriteria,
    contract_id?: string,
    contract_version?: string,
    meter_reading_type?: string,
  ): Promise<searchResult<MeterReadingValue[]>> => {
    const data = ko.toJSON({
      contract_id: contract_id,
      contract_version: contract_version,
      meter_reading_type: meter_reading_type,
      criteria: criteria,
    });
    const result = await portalHttpService.post<any>(`${this.baseApiUrl()}/SearchMeterReadings`, data);

    return new searchResult<MeterReadingValue[]>({
      count: result.count,
      result: result?.result?.map((meter) => new MeterReadingValue(meter)) || [],
    });
  };

  public saveMeterReadings = async (meter_readings: MeterReadingValue[], readingState = "SUBMITTED") => {
    const filtered = meter_readings.filter((item) => {
      return item.meter_value_new();
    });

    const data = ko.toJSON({
      meter_readings: filtered,
      reading_state: readingState,
    });
    await portalHttpService.post<any>(`${this.baseApiUrl()}/SaveMeterReadings`, data);
  };

  public exportFile = async (contract_id, contract_version, headers: { label: string }[], offset) => {
    const labelsResult = headers.map((header) => labels.descr(header.label)).join(",");
    const myUrlWithParams = new URL(`${this.baseApiUrl()}/exportProductsMeterPlansFile/`);
    myUrlWithParams.searchParams.append("contract_id", contract_id);
    myUrlWithParams.searchParams.append("contract_version", contract_version);
    myUrlWithParams.searchParams.append("timeZone", `${offset}`);
    myUrlWithParams.searchParams.append("labelsString", labelsResult);

    const fileResult: AttachmentResultDTO = await portalHttpService.get(myUrlWithParams.href);
    downloadAttachmentFromBase64(fileResult);
  };

  public exportMeterReadingsFile = async (
    contract_id,
    meter_reading_type,
    headers: { label: string }[],
    offset,
    criteria?,
  ) => {
    let labelsRequested = [labels.descr("cSequence")];
    labelsRequested = [...labelsRequested, ...headers.map((h) => labels.descr(h.label))];
    const fileResult: AttachmentResultDTO = await portalHttpService.post(
      `${this.baseApiUrl()}/exportContractMeterReadingsFile`,
      ko.toJSON({
        criteria: {
          ...criteria,
          isExport: true,
        },
        contract_id,
        meter_reading_type,
        parameters: {
          offset,
          labels: labelsRequested,
        },
      }),
    );
    downloadAttachmentFromBase64(fileResult);
  };

  public uploadBulkMeters = async (attachment): Promise<uploadMeterReading[]> => {
    var formData = new FormData();
    formData.append("imageData", dataURLtoBlob(attachment.file.filedata()));
    formData.append("attachment_name", attachment.attachment_name);
    formData.append("attachment_type", attachment.attachment_type);
    formData.append("file_type", attachment.file_type);
    formData.append("attachment_description", attachment.attachment_description);
    formData.append("customer_status", attachment.customer_status);
    formData.append("file_has_header", attachment.file_has_header);
    formData.append("test_meters_file", attachment.test_meters_file);
    formData.append("contract_id", attachment.contract_id);
    toastr.success(labels.descr("cc_P_UPLOAD_STARTED"));
    const data = await portalHttpService.ajaxPost<any>(`${this.baseApiUrl()}/uploadBulkMeters/`, formData);

    if (!attachment.test_meters_file) {
      toastr.success(labels.descr("cc_P_METER_READINGS_UPDATED"));
    }

    if (data.display_preview) {
      if (data.excelWithErrors) {
        const newdata =
          "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
          escape(data.excelWithErrors);
        const a = $("<a />");
        a.attr("download", data.file_name);
        a.attr("href", newdata);
        $("body").append(a);
        a[0].click();
        $(a).remove();
      }

      return data.file_data
        ?.map((item) => {
          if (item.reading_date?.value) {
            item.reading_date.value = moment(item.reading_date.value).format(DISPLAY_DATETIME_DEFAULT_FORMAT + " A");
          }

          return item;
        })
        .map((item) => new uploadMeterReading(item));
    }

    return null;
  };
}

function dataURLtoBlob(dataurl) {
  var arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

export const meterReadingService = new MeterReadingsService();
